/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-case-declarations */
import React, { useState, useEffect, useRef } from 'react'
import { Upload, message } from 'antd'
import { Icon } from '@iconify/react'
import {
  RcCustomRequestOptions,
  RcFile as AntRcFile,
  UploadFile as AntUploadFile
} from 'antd/lib/upload/interface'
import * as tus from 'tus-js-client'
import { produce } from 'immer'

import getUploadUrl from '@pepper/utils/src/api/files/getUploadURL'

import { Button } from '../index'
import { useShowMessage } from '../../providers'

import styles from './_.module.css'

const { Dragger } = Upload

interface UploadFile extends AntUploadFile {
  id?: string | number
  flag?: boolean
  base64?: { file: string; name: string; type: string }
}

interface RcFile extends AntRcFile {
  flag?: boolean
}

// File upload component with base64 file conversion
export interface DragFileUploadProps {
  // css className on the container
  className?: string
  // enable multiple file uploading
  multipleUpload?: boolean
  // enable multiple file selection when uploading
  multipleSelection?: boolean
  // info text for upload
  info?: React.ReactNode
  // text on the button
  caption?: string
  // setting default files on component mount, send <file> object array containing {name, id, type}
  defaultFileList?: { name: string; id: string; type: string }[]
  // enable base64 upload, if enabled, base64 property containing {file: base64string, name, type} will also be sent in handleFiles
  base64?: boolean
  // maximum size of a single file in MBs
  maxFileSize?: number
  // extension that should be accepted for uploading
  allowedFiles?: string[]
  // text at end of the input
  suffix?: string
  //To disable the upload button
  disabled?: boolean
  // handle form function to set form state sents <file> Object containing {name, id, uid, type} as parameter or array of <file> Object when multiple props is true
  handleFiles: (files: any[] | any, areAllFilesUploaded?: boolean) => void
  // type of ui
  type?: 'default' | 'minimal'
  // limit the files number
  limit?: number | null
  // show/hide list
  showList?: boolean
  // show minimal version
  minimal?: boolean
  // flag to update internal file list
  updateDefaultList?: boolean
  // callback function once default list updated
  onDefaultListUpdated?: () => void
  // Show Image preview on upload
  showImagePreview?: boolean
  renderDraggerChildren?: () => React.ReactNode
  shouldUseDefaultStyle?: boolean
  noUpload?: boolean
  onFileRemove?: () => void
  showUploadStatusToast?: boolean
  secondaryText?: React.ReactNode | string
}

// https://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript
function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['bytes', 'kb', 'mb', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + sizes[i]
}

export const DraggerUploader = ({
  className = '',
  caption = 'Browse from your computer',
  info,
  multipleUpload = false,
  multipleSelection = false,
  defaultFileList = [],
  maxFileSize = 10,
  allowedFiles = ['.docx', '.pdf', '.png', '.jpeg', 'jpeg'],
  suffix,
  disabled = false,
  handleFiles,
  type = 'default',
  limit = null,
  showList = false,
  minimal = false,
  updateDefaultList = false,
  showImagePreview = false,
  onDefaultListUpdated,
  renderDraggerChildren,
  shouldUseDefaultStyle = true,
  noUpload = false,
  onFileRemove,
  showUploadStatusToast = true,
  secondaryText = 'Drop your files here or upload manually You can add multiple files, we will scan them for the word count'
}: DragFileUploadProps): React.ReactElement => {
  const showMessage = useShowMessage()
  const _defaultFileListAdded = useRef(false)
  const [files, setFiles] = useState<any[]>([])
  const [currFileBase64] = useState<any>(null)
  const [upload, setUpload] = useState<Record<string, tus.Upload | null>>({})
  const [fileUploadStatus, setFileUploadStatus] = useState(true)

  // update filelist from parent component on component mount and add uid necessary for antd Upload component
  useEffect(() => {
    if (files.length > 0) _defaultFileListAdded.current = true
    if (
      !_defaultFileListAdded.current &&
      defaultFileList &&
      defaultFileList.length > 0 &&
      files.length === 0
    ) {
      _defaultFileListAdded.current = true
      const tempFiles: any = []
      defaultFileList.forEach((file: any) => {
        if (file?.id) tempFiles.push({ ...file, uid: file.id })
      })
      setFiles(tempFiles)
    }
  }, [files, defaultFileList])

  useEffect(() => {
    if (updateDefaultList && onDefaultListUpdated) {
      setFiles(defaultFileList)

      onDefaultListUpdated()
    }
  }, [updateDefaultList, onDefaultListUpdated, defaultFileList])

  // send files to parent component when updated
  useEffect(() => {
    // send files that have an id
    const uploadedFiles = [...files].filter(file => file.id !== undefined)
    multipleUpload ? handleFiles(uploadedFiles) : handleFiles(uploadedFiles[0])
  }, [multipleUpload, files])

  const removeFileIndex = (uid: string): void => {
    setFiles(files.filter((file, i) => file.uid !== uid))

    if (upload[uid]) {
      upload[uid]?.abort()

      setUpload(previousUpload => {
        const nextUpload = produce(previousUpload, draftState => {
          delete draftState[uid]
        })

        return nextUpload
      })
    }
  }
  // set file.flag=true if the file has to be rejected by the list
  const beforeUpload = (file: RcFile, fileList: RcFile[]) => {
    /* regex to check file type */
    const regex = new RegExp(
      `([a-zA-Z0-9s_.-:()])+(${allowedFiles.join('|')})$`
    )

    /* Handles case where a successive bulk upload result in exceeding the limit */
    if (limit && fileList.length + files.length > limit) {
      showMessage({
        type: 'error',
        message: `Maximum ${limit} files are allowed`
      })
      file.flag = true
      return false
    }

    /* handles case where the bulk upload has files exceeding the limit */

    if (limit && multipleUpload && fileList.length > limit) {
      showMessage({
        type: 'error',
        message: `Maximum ${limit} files are allowed`
      })
      file.flag = true
      return false
    }

    /* check limit of file list if limit is set */
    if (limit && files.length >= limit) {
      showMessage({
        type: 'error',
        message: `Maximum ${limit} files are allowed`
      })
      file.flag = true
      return false
    }

    /* text file type */
    if (!regex.test(encodeURIComponent(file.name).toLowerCase())) {
      showMessage({
        type: 'error',
        message: `Please upload files having extensions: ${allowedFiles.join(
          ', '
        )} only and ensure that the file name does not contain any unsupported characters.`
      })
      file.flag = true
      return false
    }

    /* check file size */
    const isSizeOkay = file.size / 1024 / 1024 < maxFileSize

    if (!isSizeOkay) {
      showMessage({
        type: 'error',
        message: `File must be smaller than ${maxFileSize}MB!`
      })
      file.flag = true
      return false
    }

    /* Check if size okay for bulk file upload */

    let isSizeOkayInBulk = true

    fileList.forEach(_file => {
      if (_file.size / 1024 / 1024 > maxFileSize) {
        showMessage({
          type: 'error',
          message: `File must be smaller than ${maxFileSize}MB!`
        })
        _file.flag = true
        isSizeOkayInBulk = false
      }
    })

    /* if all checks have passed */
    return true
  }

  const onChange = ({
    file,
    fileList
  }: {
    file: UploadFile
    fileList: UploadFile[]
  }) => {
    const { status, response, flag } = file

    // if file.flag is true then the file is rejected by beforeUpload function and update files list in parent component if corresponding function is supplied
    // https://github.com/ant-design/ant-design/issues/2423#issuecomment-233523579
    if (!flag && status !== 'removed') {
      if (multipleUpload) {
        const _fileList = [...fileList]
        _fileList.forEach(_file => {
          if (!_file.id) {
            _file.id = _file.response
            _file.base64 = currFileBase64
            _file.thumbUrl = _file.originFileObj
              ? URL.createObjectURL(_file.originFileObj)
              : ''
          }
        })
        const uploadedFiles = [..._fileList].filter(
          file => file.id !== undefined
        )
        const areAllFilesUploaded = _fileList.every(
          file => file.status === 'done'
        )
        multipleUpload
          ? handleFiles(uploadedFiles, areAllFilesUploaded)
          : handleFiles(uploadedFiles[0])
        setFiles(_fileList.filter(_file => !_file.flag))
      } else {
        file.id = response
        file.base64 = currFileBase64
        setFiles([file])
      }
    }

    if (showUploadStatusToast) {
      const fileId = file.id
      switch (status) {
        case 'done':
          showMessage({
            type: 'success',
            message: 'File uploaded successfully.'
          })
          message.destroy('loading')
          break
        case 'uploading':
          message.loading({ content: 'Uploading...', key: 'loading' })
          break
        case 'removed':
          setFiles(files.filter((_file: any) => fileId !== _file.id))
          message.destroy('loading')
          break
        case 'error':
          showMessage({ type: 'error', message: response })
          message.destroy('loading')
          break
        default:
          setFiles(files.filter((_file: any) => fileId !== _file.id))
          break
      }
    }
  }

  const props = {
    accept: allowedFiles.join(),
    multiple: multipleSelection || false,
    beforeUpload,
    disabled: disabled || false,
    showUploadList: false,
    onChange,
    customRequest: async ({
      onProgress,
      onSuccess,
      onError,
      file
    }: RcCustomRequestOptions): Promise<void> => {
      try {
        const result = await getUploadUrl(file)

        if (noUpload) {
          return onSuccess(result.data.id, file)
        }

        if (result.status === 'error') {
          onError(new Error(result.error))
          throw new Error(result.error)
        }

        const _upload = new tus.Upload(file, {
          uploadUrl: result.data.upload_url,
          retryDelays: [0, 1000, 3000, 5000],
          onError: error => {
            console.error('[error]', error)
            onError(error)
          },
          onProgress: (bytesUploaded, bytesTotal) => {
            const percent = +Math.round(
              (bytesUploaded / bytesTotal) * 100
            ).toFixed(2)
            onProgress({ percent }, file)
          },
          onSuccess: () => {
            onSuccess(result.data.id, file)
          }
        })

        _upload.start()

        setUpload(_previousUploads => {
          const nextUpload = produce(_previousUploads, draftState => {
            draftState[file.uid] = _upload
          })

          return nextUpload
        })
      } catch (error) {
        setFileUploadStatus(false)
        onError(error)
        showMessage({ type: 'error', message: 'File upload failed.' })
      }
    }
  }

  const isUploading = (_files: UploadFile[] = []) => {
    let isUploading = false
    _files.forEach(_file => {
      if (!isUploading) {
        isUploading = _file.status === 'uploading'
      }
    })
    return isUploading
  }

  const handleFileRemoval = (fileUId: string) => {
    onFileRemove?.()
    removeFileIndex(fileUId)
  }

  return (
    <div className={className}>
      {showList && isUploading(files) ? (
        <p className={styles.uploadingText}>
          <Icon icon="ant-design:loading-outlined" /> Uploading...
        </p>
      ) : (
        <div>
          {minimal ? (
            <div
              className={
                shouldUseDefaultStyle ? styles.minimal_dragDropSection : ''
              }
            >
              <Dragger {...props} fileList={files}>
                {renderDraggerChildren ? (
                  renderDraggerChildren()
                ) : (
                  <>
                    <div className={styles.iconWithText}>
                      <Icon
                        icon="ant-design:inbox-outlined"
                        className={styles.minimal_uploadBox}
                      />
                      <p className={styles.minimal_dragDropText}>
                        Drop your files here or upload manually
                      </p>
                    </div>
                    <Button
                      htmlType="button"
                      className={styles.minimal_uploadButton}
                      type="link"
                      disabled={disabled}
                    >
                      {caption}
                    </Button>
                  </>
                )}
              </Dragger>
            </div>
          ) : (
            <Dragger
              className={styles.dragDropSection}
              {...props}
              fileList={files}
            >
              {renderDraggerChildren ? (
                renderDraggerChildren()
              ) : (
                <>
                  <Icon
                    icon="ant-design:inbox-outlined"
                    className={styles.uploadBox}
                  />
                  <p className={styles.dragDropText}>{secondaryText}</p>
                  <Button
                    htmlType="button"
                    className={styles.uploadButton}
                    type="link"
                  >
                    {caption}
                  </Button>
                </>
              )}
            </Dragger>
          )}

          {info && <p className={styles.infoText}>{info}</p>}
        </div>
      )}

      {/* custom file list */}
      {showList && fileUploadStatus ? (
        <ol className={styles.list}>
          {files.map((file: UploadFile, index: number) => {
            return (
              <li key={file.uid}>
                <span>
                  {index + 1}.&nbsp;&nbsp;
                  {file.name}
                  {file.size && (
                    <span className={styles.listSize}>
                      {formatBytes(file.size, 0)}
                    </span>
                  )}
                </span>

                <span>
                  {file.status === 'uploading' ? (
                    <Icon icon="ant-design:loading-outlined" />
                  ) : (
                    <Button
                      size="small"
                      type="text"
                      onClick={() => handleFileRemoval(file.uid)}
                      disabled={disabled}
                    >
                      <Icon icon="ant-design:delete-outlined" />
                    </Button>
                  )}
                </span>
              </li>
            )
          })}
        </ol>
      ) : (
        ''
      )}
    </div>
  )
}
